import ActionButton from "controls/global/action-button";
import React, { ReactNode, useEffect, useState } from "react";
import { colors, fontFamily, fontSize, fontWeight, padding } from "theme/defaultStyle";

import { IconButton, styled } from "@mui/material";
import { Box } from "@mui/system";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { StewartDialog, StewartDialogActions, StewartDialogContent } from "controls/global/dialogs/stewart-dialog/StewartDialog";

type IconType = "delete" | "info" | "warning";

const CustomDialogContent = styled(StewartDialogContent)({
    maxHeight: "70vh",
    overflowY: "auto",
    overflowWrap: "break-word",
    padding: "32px 32px 16px 32px !important",
    "@media (min-width: 600px)": {
        padding: "32px 32px 16px 32px !important",
    },
    display: "flex",
    flexDirection: "column",
    gap: "0px",
});

const ContentWrapper = styled(Box)<{ hasSubtitle: boolean }>(({ hasSubtitle }) => ({
    display: "flex",
    alignItems: hasSubtitle ? "flex-start" : "center",
    gap: "20px",
    paddingRight: "48px",
    "@media (max-width: 600px)": {
        flexDirection: "row",
        alignItems: hasSubtitle ? "flex-start" : "center",
        gap: "16px",
        paddingRight: "40px",
    },
}));

const StyledStewartDialog = styled(StewartDialog)({
    "& .MuiPaper-root": {
        width: "100%",
        maxWidth: "720px",
        margin: "16px",
        borderRadius: "16px",
        padding: "8px",
        minHeight: "auto",
        height: "230px",
        "@media (min-width: 600px)": {
            width: "680px",
            margin: "32px",
        },
        "& .MuiDialogActions-root": {
            justifyContent: "flex-end",
            padding: "16px 32px 32px 32px",
            gap: "12px",
            flexWrap: "wrap",
            "@media (min-width: 600px)": {
                flexWrap: "nowrap",
            },
        },
    },
});

const IconBox = styled(Box)<{ iconType: IconType }>(({ iconType }) => ({
    backgroundImage:
        iconType === "delete"
            ? "linear-gradient(180deg, #FFC400 0%, #FFAB00 100%)"
            : iconType === "warning"
                ? "linear-gradient(180deg, #FFC400 0%, #FFAB00 100%)"
                : "linear-gradient(180deg, #FFC400 0%, #FFAB00 100%)",
    color: colors.grey08,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid transparent",
    borderRadius: "12px",
    flexShrink: 0,
    width: "56px",
    height: "56px",
    "& svg": {
        fontSize: "28px",
        color: "#FFFFFF",
    },
}));

const TextContent = styled(Box)<{ hasSubtitle: boolean }>(({ hasSubtitle }) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    marginTop: "10px",
    minWidth: 0,
    paddingTop: hasSubtitle ? "2px" : "0px",
    "@media (max-width: 600px)": {
        textAlign: "left",
        gap: "8px",
    },
}));

const Title = styled(Box)({
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "32px",
    color: "#212121",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    "@media (max-width: 600px)": {
        fontSize: "20px",
        lineHeight: "28px",
    },
});

const Subtitle = styled(Box)({
    fontSize: fontSize.base,
    fontStyle: fontFamily.inter,
    fontWeight: fontWeight.normal1,
    color: colors.grey08,
    wordWrap: "break-word",
    overflowWrap: "break-word",
    "@media (max-width: 600px)": {
        fontSize: "14px",
        lineHeight: "20px",
    },
});

const CloseIconContainer = styled("div")({
    background: "#F5F5F5",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: "24px",
    right: "24px",
    width: "40px",
    height: "40px",
    padding: "0px",
    zIndex: 1,
    cursor: "pointer",
    flexShrink: 0,
    "@media (min-width: 600px)": {
        top: "36px",
        right: "36px",
    },
    "&:hover": {
        backgroundColor: "#E0E0E0",
    },
    "& .MuiSvgIcon-root": {
        color: "#424242",
        width: "24px",
        height: "24px",
        fontWeight: 400,
    },
    "& .iconButton": {
        padding: padding.zero,
        backgroundColor: "transparent",
        width: "100%",
        height: "100%",
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
});

const StyledActionButton = styled(ActionButton)({
    minWidth: "100px",
    height: "40px",
    fontSize: "15px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "6px",
    padding: "8px 24px",
    boxShadow: "none",
    "@media (max-width: 600px)": {
        minWidth: "90px",
        fontSize: "14px",
    },
});

type Props = {
    title: string | ReactNode;
    subtitle?: string | ReactNode;
    iconType?: IconType;
    isOpen: boolean;
    onPrimaryAction: () => void;
    primaryButtonText?: string;
    primaryButtonColor?: "primary" | "secondary";
    disablePrimaryButton?: boolean;
    onSecondaryAction?: () => void;
    secondaryButtonText?: string;
    disableSecondaryButton?: boolean;
    showCloseIcon?: boolean;
    onClose?: () => void;
    width?: number;
};

const SignatureConfirmationDialog = ({
    title,
    subtitle,
    iconType = "info",
    isOpen,
    onPrimaryAction,
    primaryButtonText = "Confirm",
    primaryButtonColor = "primary",
    disablePrimaryButton = false,
    onSecondaryAction,
    secondaryButtonText = "Cancel",
    disableSecondaryButton = false,
    showCloseIcon = true,
    onClose,
    width=530,
}: Props) => {
    const [open, setOpen] = useState(false);
    const hasSubtitle = Boolean(subtitle);

    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const getIcon = () => {
        switch (iconType) {
            case "delete":
                return <DeleteIcon />;
            case "warning":
                return <WarningAmberIcon />;
            case "info":
            default:
                return <InfoOutlinedIcon />;
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    return (
        <StyledStewartDialog open={open} onClose={handleClose} width={width}>
            <CustomDialogContent>
                {showCloseIcon && (
                    <CloseIconContainer>
                        <IconButton className="iconButton" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </CloseIconContainer>
                )}
                <ContentWrapper hasSubtitle={hasSubtitle}>
                    <IconBox iconType={iconType}>{getIcon()}</IconBox>
                    <TextContent hasSubtitle={hasSubtitle}>
                        <Title>{title}</Title>
                        {subtitle && <Subtitle>{subtitle}</Subtitle>}
                    </TextContent>
                </ContentWrapper>
            </CustomDialogContent>
            <StewartDialogActions>
                {onSecondaryAction && (
                    <StyledActionButton
                        variant="outlined"
                        color="secondary"
                        onClick={onSecondaryAction}
                        disabled={disableSecondaryButton}
                    >
                        {secondaryButtonText}
                    </StyledActionButton>
                )}
                <StyledActionButton
                    autoFocus
                    variant="outlined"
                    color={primaryButtonColor}
                    onClick={onPrimaryAction}
                    disabled={disablePrimaryButton}
                >
                    {primaryButtonText}
                </StyledActionButton>
            </StewartDialogActions>
        </StyledStewartDialog>
    );
};

export default SignatureConfirmationDialog;